import { bodyAnchorOutput, bodyAssessment, bodyAssessmentCreated, bodyAssessmentListOutput, bodyAssessmentPath, bodyAssessmentPhotoPath, bodyPhotoConsentOutput, TRAINING_ROUTES, } from "@repo/contract/training";
import { toApiError } from "../../api/api-error";
import { readJson } from "../../api/auth-api";
import { getJson, sendJson, sendNoContent, } from "../core/request";
import { bodyKeys, bodyRevokeInvalidates } from "./keys";
/**
 * `@repo/client/data/body` — the body-photo assessment, app side (`D16`–`D26`,
 * which amend `D99`): the consent record, the two photos going up, the number
 * coming back, one photo coming back down, and the personal anchor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ── FOUR THINGS THAT FAIL SILENTLY IF A CALLER GETS THEM WRONG ─────────────
 *
 * **1. A 503 `body_assessment.unavailable` on the create means the photographs
 * were ACCEPTED.** The row is written and both photos are in the bucket before
 * the submit is attempted; the 503 says only that `bodycomp` would not take the
 * job right now (`T97`, decision 1). The server's own poll settles it within
 * the minute, and a resend of the same `meta.id` answers 200 with the stored
 * row and `created: false` (`D163`). So a screen must NEVER offer to retake the
 * photographs on this status — that is two uploads, two jobs on a queue that
 * allows twenty a minute for everybody (`D26`) and two rows in a trend the
 * person reads as their body changing.
 *
 * **2. There is no outbox here, deliberately.** The outbox persists JSON in
 * Preferences and cannot hold a `Blob` (`offline/store.ts`), and an assessment
 * needs the network anyway — the photos are in memory and nowhere else once the
 * screen closes. What replaces it is the client-minted `meta.id`: retrying the
 * same call with the SAME id is safe and idempotent, so the retry belongs to
 * the screen that still holds the two `Blob`s.
 *
 * **3. `meta.id` is minted by the CALLER, with the `uuidv7` helper, never with
 * `crypto.randomUUID()`** — that does not exist on the live-reload origin
 * (`T-174`), which is the one origin this feature will first be tried on. It is
 * not minted here for the reason `recordMeasurement`'s key is not: a key minted
 * inside the call is a fresh key per retry, the exact duplicate it exists to
 * prevent.
 *
 * **4. The photo route answers BYTES behind the bearer header.** An
 * `<img src>` pointed straight at it sends no `Authorization` and 401s.
 * {@link fetchBodyAssessmentPhoto} is the only way in; the caller makes an
 * object URL and revokes it on unmount.
 *
 * ── THE BODY IS A `FormData`, AND WHY THAT IS STILL REPLAYABLE ─────────────
 * `core/request.ts` sends strings only, because `authorizedFetch` resends the
 * request once after a 401 refresh and a stream would be spent by the first
 * send. `transcribe` is the standing exception for a `Blob`; a `FormData` whose
 * parts are a string and two `Blob`s is exactly as replayable — nothing in it
 * is consumed by being sent — so the create brings its own function rather than
 * widening those helpers.
 *
 * ── AND NEVER SET `content-type` ON IT ─────────────────────────────────────
 * The runtime writes `multipart/form-data; boundary=…` from the body itself. A
 * hand-written header omits the boundary, and the server then reads a body with
 * no parts at all: `request.invalid_body`, on a request that looks right in
 * every log.
 *
 * ── THE SERVER HALF IS IN THE TREE AND NOT YET DEPLOYED ────────────────────
 * Nothing here may ship before the API that answers it reaches an environment:
 * a field an older server does not know answers 422 `request.invalid_body`
 * (`D163`, `D172`).
 * ═══════════════════════════════════════════════════════════════════════════
 */
// ── consent ────────────────────────────────────────────────────────────────
/**
 * `GET /me/body-photo-consent` — the record, or `{ consent: null }`.
 *
 * `null` means never granted OR revoked, and the screen treats them the same:
 * the offer, with the text. `consent.current` is the SERVER's comparison
 * against its own {@link BODY_PHOTO_CONSENT_VERSION} and not this app's — an
 * app older than a bump would otherwise show a green tick and be refused at the
 * shutter with `body_assessment.consent_outdated`.
 */
export function fetchBodyPhotoConsent(api, init) {
    return getJson(api, TRAINING_ROUTES.bodyPhotoConsent, bodyPhotoConsentOutput, init);
}
/**
 * `POST /me/body-photo-consent` — the person agreed to the text they were
 * SHOWN, so the version travels rather than being assumed server-side.
 *
 * 201 for a new grant, 200 when the same version is already active; both answer
 * the record, so a double-tap is not a second consent and this function does
 * not distinguish them.
 */
export function grantBodyPhotoConsent(api, textVersion) {
    return sendJson(api, "POST", TRAINING_ROUTES.bodyPhotoConsent, { textVersion }, bodyPhotoConsentOutput);
}
/**
 * `DELETE /me/body-photo-consent` — 204, and it **deletes every photo while
 * keeping every number** (`D20`). Idempotent: a replay is another 204, never a
 * 404 (`T97`, decision 12).
 */
export function revokeBodyPhotoConsent(api) {
    return sendNoContent(api, "DELETE", TRAINING_ROUTES.bodyPhotoConsent);
}
export function bodyPhotoConsentOptions(api) {
    return {
        queryFn: ({ signal }) => fetchBodyPhotoConsent(api, { signal }),
        queryKey: bodyKeys.consent,
    };
}
export function grantBodyPhotoConsentMutation(api) {
    return {
        invalidates: [bodyKeys.consent],
        mutationFn: (textVersion) => grantBodyPhotoConsent(api, textVersion),
        mutationKey: ["body", "consent", "grant"],
    };
}
/** Invalidates the WHOLE family — see {@link bodyRevokeInvalidates}. */
export function revokeBodyPhotoConsentMutation(api) {
    return {
        invalidates: bodyRevokeInvalidates(),
        mutationFn: () => revokeBodyPhotoConsent(api),
        mutationKey: ["body", "consent", "revoke"],
    };
}
/** `image/jpeg`, the one type the route accepts and the magic bytes prove. */
const JPEG_CONTENT_TYPE = "image/jpeg";
/**
 * The parts as a `FormData`: `meta` as JSON TEXT, `front` and `side` as
 * `image/jpeg`.
 *
 * `meta` is appended as a string and not as a `Blob`, because the server reads
 * it with `typeof value === "string"` — a JSON `Blob` arrives as a `File` there
 * and is refused as `request.invalid_body`. The two photos are re-wrapped with
 * an explicit type and a filename: a `Blob` from a `<canvas>` carries
 * `image/jpeg` already, but one read off a file input does not always, and the
 * route checks the magic bytes anyway.
 */
export function bodyAssessmentFormData(input) {
    const form = new FormData();
    form.append("meta", JSON.stringify(input.meta));
    form.append("front", new Blob([input.front], { type: JPEG_CONTENT_TYPE }), "front.jpg");
    form.append("side", new Blob([input.side], { type: JPEG_CONTENT_TYPE }), "side.jpg");
    return form;
}
/**
 * `POST /me/body-assessments` — two photos up, one assessment back.
 *
 * 201 on a create, 200 on a replay of the same `meta.id`. Every refusal arrives
 * as the server's own `ApiError`: `body_assessment.consent_required` /
 * `consent_outdated` (409), `age_out_of_range` / `sex_required` (422),
 * `photo_missing` (400), `photo_too_large` (413), `photo_unsupported` (415),
 * `too_many_requests` (429, **the person's own ten an hour**, not the tenant's
 * — `T97`, decision 3) and `unavailable` (503, **the photographs were
 * accepted** — see the module header).
 *
 * No `content-type` header, ever: see the module header.
 */
export async function createBodyAssessment(api, input) {
    const response = await api.authorizedFetch(TRAINING_ROUTES.bodyAssessments, {
        body: bodyAssessmentFormData(input),
        method: "POST",
    });
    const createdStatus = response.status === 201;
    const answer = await readJson(response, bodyAssessmentCreated);
    return { ...answer, createdStatus };
}
/**
 * Invalidates the list, every detail and every photo — a new row appears and
 * the poll below settles it — plus the anchor, because a create carrying a
 * `reference` is the ONE thing that sets or replaces one (`D23`; the anchor has
 * no `PUT`, deliberately).
 */
export function createBodyAssessmentMutation(api) {
    return {
        invalidates: [bodyKeys.assessments, bodyKeys.anchor],
        mutationFn: (input) => createBodyAssessment(api, input),
        mutationKey: ["body", "assessments", "create"],
    };
}
// ── reading them back ──────────────────────────────────────────────────────
/** `GET /me/body-assessments` — newest first, at most 200, never polls. */
export function fetchBodyAssessments(api, init) {
    return getJson(api, TRAINING_ROUTES.bodyAssessments, bodyAssessmentListOutput, init);
}
/** `GET /me/body-assessments/:id` — the read, with the server's read-through
 *  poll while the row is `pending`. Unknown, another account's and deleted are
 *  one 404 `body_assessment.not_found`, on purpose. */
export function fetchBodyAssessment(api, id, init) {
    return getJson(api, bodyAssessmentPath(id), bodyAssessment, init);
}
/** `DELETE /me/body-assessments/:id` — 204, soft-deletes the row and deletes
 *  both photos. Idempotent, so a replay is not a 404. */
export function deleteBodyAssessment(api, id) {
    return sendNoContent(api, "DELETE", bodyAssessmentPath(id));
}
export function bodyAssessmentsOptions(api) {
    return {
        queryFn: ({ signal }) => fetchBodyAssessments(api, { signal }),
        queryKey: bodyKeys.assessmentList,
    };
}
export function deleteBodyAssessmentMutation(api) {
    return {
        invalidates: [bodyKeys.assessments],
        mutationFn: (id) => deleteBodyAssessment(api, id),
        mutationKey: ["body", "assessments", "delete"],
    };
}
// ── the poll ───────────────────────────────────────────────────────────────
/** Three seconds while a row is `pending`. The job takes two to six seconds. */
export const BODY_ASSESSMENT_POLL_MS = 3000;
/**
 * How long the APP keeps asking. Three minutes, and the number is a UI budget
 * rather than a deadline: `pollPendingBodyAssessments` runs on the server's
 * cron and settles the row whether or not anything is watching, so giving up
 * here loses nothing but the live update. A screen that has waited three
 * minutes shows what it has and refetches when it is opened again.
 */
export const BODY_ASSESSMENT_POLL_WINDOW_MS = 3 * 60_000;
/**
 * The poll policy, as a pure function of the row and the clock: `3000` while
 * the row is `pending` and within the window, `false` otherwise.
 *
 * Measured from `createdAt` and NOT from when the options object was built,
 * which is the difference that matters when a screen is reopened on a row that
 * has been pending for an hour: from `createdAt` it does not start a fresh
 * three-minute poll on a job that is long past helping.
 *
 * `undefined` data is `3000` — the first fetch has not landed, and answering
 * `false` there would need the answer again the moment it did.
 */
export function bodyAssessmentPollInterval(row, now) {
    if (row === undefined) {
        return BODY_ASSESSMENT_POLL_MS;
    }
    if (row.status !== "pending") {
        return false;
    }
    const age = now - Date.parse(row.createdAt);
    return Number.isNaN(age) || age < BODY_ASSESSMENT_POLL_WINDOW_MS
        ? BODY_ASSESSMENT_POLL_MS
        : false;
}
export function bodyAssessmentOptions(api, id) {
    return {
        queryFn: ({ signal }) => fetchBodyAssessment(api, id, { signal }),
        queryKey: bodyKeys.assessment(id),
        refetchInterval: bodyAssessmentPollInterval,
    };
}
// ── one photo ──────────────────────────────────────────────────────────────
/**
 * `GET /me/body-assessments/:id/photos/:view` — the JPEG itself.
 *
 * Fetched rather than pointed at, because the route needs the bearer header and
 * an `<img src>` carries none (`T97`). The caller makes the object URL and owns
 * revoking it:
 *
 * ```ts
 * const url = URL.createObjectURL(await fetchBodyAssessmentPhoto(api, id, "front"));
 * // …and on unmount: URL.revokeObjectURL(url)
 * ```
 *
 * A photo deleted by a revocation, a delete or an account erasure is a 404
 * `body_assessment.not_found`, the same as an unknown id — so
 * `assessment.photos.front` is what a screen reads to decide whether to ask at
 * all, and the 404 is the race, not the normal path.
 *
 * `readJson` is deliberately not used: the body is not JSON. A non-2xx still
 * throws the server's own `ApiError`.
 */
export async function fetchBodyAssessmentPhoto(api, id, view, init) {
    const response = await api.authorizedFetch(bodyAssessmentPhotoPath(id, view), {
        method: "GET",
        ...(init?.signal === undefined ? {} : { signal: init.signal }),
    });
    if (!response.ok) {
        throw await toApiError(response);
    }
    return await response.blob();
}
// ── the anchor ─────────────────────────────────────────────────────────────
/** `GET /me/body-anchor` — the personal calibration as an OFFSET in percentage
 *  points, or `{ anchor: null }`. There is no write: an anchor is born inside a
 *  settle, from a `reference` sent with a create (`D23`). */
export function fetchBodyAnchor(api, init) {
    return getJson(api, TRAINING_ROUTES.bodyAnchor, bodyAnchorOutput, init);
}
/** `DELETE /me/body-anchor` — 204, idempotent. Later assessments go back to the
 *  calibrated range; the rows already computed keep their numbers, which is why
 *  this invalidates the anchor alone. */
export function clearBodyAnchor(api) {
    return sendNoContent(api, "DELETE", TRAINING_ROUTES.bodyAnchor);
}
export function bodyAnchorOptions(api) {
    return {
        queryFn: ({ signal }) => fetchBodyAnchor(api, { signal }),
        queryKey: bodyKeys.anchor,
    };
}
export function clearBodyAnchorMutation(api) {
    return {
        invalidates: [bodyKeys.anchor],
        mutationFn: () => clearBodyAnchor(api),
        mutationKey: ["body", "anchor", "clear"],
    };
}
export { bodyKeys, bodyRevokeInvalidates } from "./keys";
//# sourceMappingURL=index.js.map